import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, Store, Sparkles, MessageCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

const Shop = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchShopData = async () => {
      try {
        const { data: links, error } = await supabase
          .from("marketplace_links")
          .select("*")
          .eq("title", username)
          .maybeSingle();

        if (error) throw error;
        
        if (!links) {
          setNotFound(true);
        } else {
          setData(links);
        }
      } catch (error) {
        console.error("Error fetching shop:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchShopData();
  }, [username]);

  const handleWhatsAppClick = () => {
    if (!data) return;
    const message = data.message_template.replace("{product}", data.title);
    const whatsappUrl = `https://wa.me/${data.whatsapp_number}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary">Memuat...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Link Tidak Ditemukan</AlertTitle>
          <AlertDescription>
            Maaf, link yang Anda cari tidak tersedia. Pastikan URL yang Anda masukkan benar atau kembali ke halaman utama.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-green-100/50 to-white dark:from-green-900/30 dark:via-green-800/20 dark:to-black/20">
      <div className="container max-w-2xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12 space-y-6 animate-fade-down">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white shadow-xl">
            <Store className="w-12 h-12" />
          </div>
          <div className="space-y-3">
            <h1 className="text-4xl font-black bg-gradient-to-r from-green-600 via-green-500 to-emerald-400 bg-clip-text text-transparent animate-text">
              {data.title}
            </h1>
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-green-500" />
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Selamat datang di toko {data.title}
              </p>
              <Sparkles className="w-5 h-5 text-green-500" />
            </div>
          </div>
        </div>

        {/* WhatsApp Button Section */}
        <div className="space-y-6 animate-fade-up">
          <div className="bg-white/50 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-green-100/50 dark:border-white/10">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                    <span className="text-2xl">✨</span>
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                    {data.title}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    https://jual.in/{data.title}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleWhatsAppClick}
                className="w-full h-14 text-lg bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-600 hover:to-emerald-500 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <MessageCircle className="w-6 h-6 mr-2 transition-transform group-hover:scale-110" />
                Hubungi via WhatsApp
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400 animate-fade-up">
          Dibuat dengan ❤️ menggunakan{" "}
          <a href="/" className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-semibold">
            jual.in
          </a>
        </div>
      </div>
    </div>
  );
};

export default Shop;